import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Printer,
    Truck,
    ExternalLink,
    Trash2,
    Calendar as CalendarIcon,
    User,
    ChevronRight,
    ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ToursHistoryPage = () => {
    const { token } = useAuthStore();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // 1. Récupération de l'historique
    const { data: tours = [], isLoading } = useQuery({
        queryKey: ['tours'],
        queryFn: async () => {
            const res = await fetch(`${API_URL}/tours`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();

            return data.map(t => ({ ...t, id: t.id || t._id }));
        },
    });

    // 2. Mutation pour supprimer une tournée
    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            if (!id || id === 'undefined') {
                throw new Error("ID de tournée invalide");
            }
            await fetch(`${API_URL}/tours/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['tours']);
            toast.success('Supprimé');
        }
    });

    // 3. Fonction de téléchargement du PDF
    const downloadPDF = async (tourId, ref) => {
        const loadingToast = toast.loading("Génération du PDF...");
        try {
            const res = await fetch(`${API_URL}/tours/${tourId}/pdf`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error();

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `tournee_${ref}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            toast.dismiss(loadingToast);
            toast.success("PDF téléchargé");
        } catch (e) {
            toast.dismiss(loadingToast);
            toast.error("Erreur lors de la génération du PDF");
        }
    };

    return (
        <div className="space-y-6 flex flex-col h-[calc(100vh-120px)]">
            {/* HEADER */}
            <div className="flex items-center justify-between shrink-0">
                <div className="flex items-center gap-4">
                    <Button variant="outline" onClick={() => navigate('/deliveries')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        <span className="hidden sm:inline">Retour aux livraisons</span>
                    </Button>
                    <div>
                        <h1 className="text-2xl font-heading font-bold">Historique des Tournées</h1>
                        <p className="text-muted-foreground text-sm">Gestion des trajets et feuilles de route</p>
                    </div>
                </div>

                <Badge variant="secondary" className="px-3 py-1">
                    {tours.length} tournée(s) au total
                </Badge>
            </div>

            {/* LISTE DES TOURNÉES */}
            <div className="flex-1 overflow-y-auto pr-2 space-y-4 pb-10 custom-scrollbar">
                {isLoading ? (
                    <div className="space-y-4">
                        <Skeleton className="h-24 w-full" />
                        <Skeleton className="h-24 w-full" />
                        <Skeleton className="h-24 w-full" />
                    </div>
                ) : tours.length > 0 ? (
                    tours.map((tour) => (
                        <Card key={tour.id || tour._id} className="hover:border-primary/50 transition-all shadow-sm">
                            <CardContent className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">

                                {/* Infos Tournée */}
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-xl text-primary">
                                        <Truck className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-bold">{tour.reference}</h3>
                                            <Badge variant="secondary" className="text-[10px]">{tour.delivery_ids?.length || 0} arrêts</Badge>
                                        </div>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                                            <span className="flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" /> {formatDate(tour.date)}</span>
                                            <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {tour.driver_name}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2 w-full md:w-auto">
                                    {/* Bouton PDF */}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadPDF(tour.id, tour.reference)}
                                        className="flex-1 md:flex-none"
                                    >
                                        <Printer className="w-4 h-4 mr-2" /> PDF
                                    </Button>

                                    {/* Bouton Copier Lien */}
                                    <Button variant="outline" size="sm" onClick={() => {
                                        const url = `${window.location.origin}/portal/tour/${tour.token}`;
                                        navigator.clipboard.writeText(url);
                                        toast.success("Lien copié !");
                                    }}>
                                        <ExternalLink className="w-4 h-4 mr-2" /> Lien
                                    </Button>

                                    {/* Bouton Voir Portai */}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => window.open(`${window.location.origin}/portal/tour/${tour.token}`, '_blank')}
                                        title="Voir le portail livreur"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </Button>

                                    {/* Bouton Supprimer */}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="text-destructive hover:bg-destructive/10"
                                        onClick={() => window.confirm('Supprimer cet historique ?') && deleteMutation.mutate(tour.id)}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>

                            </CardContent>
                        </Card>
                    ))
                ) : (
                    <div className="text-center py-20 bg-muted/20 rounded-xl border-2 border-dashed">
                        <Truck className="w-12 h-12 mx-auto mb-4 opacity-20" />
                        <p className="text-muted-foreground">Aucune tournée enregistrée.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ToursHistoryPage;